import { useEffect, useState } from "react";
import { useGetList, useGetIdentity, useRedirect } from "ra-core";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
    Sparkles,
    MessageSquare,
    Phone,
    TrendingUp,
    ArrowRight,
    Sun,
    CheckCircle2
} from "lucide-react";
import { cn } from "@/lib/utils";
import type { Contact, Deal } from "../types";

// Types for our "Insights"
interface Insight {
    id: string;
    type: "urgent_sms" | "stalled_deal" | "hot_lead";
    title: string;
    description: string;
    actionLabel: string;
    actionLink: string;
    priority: "high" | "medium" | "low";
}

export const MorningPulse = () => {
    const { identity } = useGetIdentity();
    const redirect = useRedirect();
    const [greeting, setGreeting] = useState("Good morning");
    const [insights, setInsights] = useState<Insight[]>([]);

    // 1. Fetch recent inbound SMS to find unanswered ones
    // We fetch the last 20 messages to scan for opportunities
    const { data: messages, isPending: msgsPending } = useGetList("sms_messages", {
        pagination: { page: 1, perPage: 20 },
        sort: { field: "created_at", order: "DESC" },
        filter: { direction: "inbound" }
    });

    // 2. Fetch "Hot" leads that haven't been touched recently
    const { data: hotLeads, isPending: leadsPending } = useGetList<Contact>("contacts", {
        pagination: { page: 1, perPage: 5 },
        sort: { field: "last_seen", order: "ASC" }, // Oldest interaction first
        filter: { status: "hot" }
    });

    // 3. Fetch Deals that are high value
    const { data: highValueDeals, isPending: dealsPending } = useGetList<Deal>("deals", {
        pagination: { page: 1, perPage: 3 },
        sort: { field: "amount", order: "DESC" },
        filter: { stage: "negotiation" } // Or whatever active stage
    });

    useEffect(() => {
        const hour = new Date().getHours();
        if (hour < 12) setGreeting("Good morning");
        else if (hour < 18) setGreeting("Good afternoon");
        else setGreeting("Good evening");
    }, []);

    useEffect(() => {
        if (msgsPending || leadsPending || dealsPending) return;

        const newInsights: Insight[] = [];

        // Logic 1: Find urgent SMS (simulated "unanswered" logic for now)
        // In a real app, we'd check if there's a reply. For now, take the top 2 recent inbound.
        if (messages && messages.length > 0) {
            const urgentMsg = messages[0];
            newInsights.push({
                id: `sms-${urgentMsg.id}`,
                type: "urgent_sms",
                title: "Reply waiting",
                description: `Received a message that needs attention.`,
                actionLabel: "Reply now",
                actionLink: `/contacts/${urgentMsg.contact_id}/show`,
                priority: "high"
            });
        }

        // Logic 2: Hot Lead slipping away
        if (hotLeads && hotLeads.length > 0) {
            const lead = hotLeads[0];
            newInsights.push({
                id: `lead-${lead.id}`,
                type: "hot_lead",
                title: "Hot lead cooling down",
                description: `${lead.first_name} ${lead.last_name || ""} hasn't been contacted in a while.`,
                actionLabel: "View Lead",
                actionLink: `/contacts/${lead.id}/show`,
                priority: "medium"
            });
        }

        // Logic 3: High Value Deal
        if (highValueDeals && highValueDeals.length > 0) {
            const deal = highValueDeals[0];
            newInsights.push({
                id: `deal-${deal.id}`,
                type: "stalled_deal",
                title: "Close this deal",
                description: `${deal.name} is worth $${deal.amount.toLocaleString()}. Push it over the line!`,
                actionLabel: "View Deal",
                actionLink: `/deals/${deal.id}/show`,
                priority: "medium"
            });
        }

        setInsights(newInsights);
    }, [messages, hotLeads, highValueDeals, msgsPending, leadsPending, dealsPending]);

    const handleAction = (link: string) => {
        redirect(link);
    };

    if (msgsPending || leadsPending) return null;

    return (
        <Card className="border-0 bg-gradient-to-r from-indigo-50 to-blue-50 dark:from-slate-900 dark:to-slate-800 border-l-4 border-l-indigo-500 shadow-sm mb-6 overflow-hidden relative">
            {/* Background decoration */}
            <div className="absolute top-0 right-0 p-8 opacity-10">
                <Sparkles className="w-32 h-32 text-indigo-500" />
            </div>

            <CardContent className="p-6 relative z-10">
                <div className="flex flex-col md:flex-row md:items-start justify-between gap-6">

                    {/* Welcome Section */}
                    <div className="space-y-2 max-w-md">
                        <div className="flex items-center gap-2 text-indigo-600 dark:text-indigo-400 font-medium text-sm uppercase tracking-wider">
                            <Sun className="w-4 h-4" />
                            <span>Morning Briefing</span>
                        </div>
                        <h2 className="text-2xl font-bold text-slate-800 dark:text-slate-100">
                            {greeting}, {identity?.fullName || "User"}.
                        </h2>
                        <p className="text-slate-600 dark:text-slate-300 leading-relaxed">
                            {insights.length > 0
                                ? "I've analyzed your pipeline. Here are the top priorities to focus on right now to maximize your momentum."
                                : "Your pipeline is looking clean! Great job staying on top of things."}
                        </p>
                    </div>

                    {/* Insights Grid */}
                    <div className="flex-1 grid gap-3 w-full">
                        {insights.map((insight) => (
                            <div
                                key={insight.id}
                                className="bg-white/60 dark:bg-slate-800/60 backdrop-blur-sm p-3 rounded-lg border border-slate-100 dark:border-slate-700 flex items-center justify-between group hover:border-indigo-200 dark:hover:border-indigo-900 transition-all cursor-pointer"
                                onClick={() => handleAction(insight.actionLink)}
                            >
                                <div className="flex items-center gap-3">
                                    <div className={cn(
                                        "w-10 h-10 rounded-full flex items-center justify-center shrink-0",
                                        insight.type === "urgent_sms" ? "bg-blue-100 text-blue-600" :
                                            insight.type === "hot_lead" ? "bg-orange-100 text-orange-600" :
                                                "bg-green-100 text-green-600"
                                    )}>
                                        {insight.type === "urgent_sms" && <MessageSquare className="w-5 h-5" />}
                                        {insight.type === "hot_lead" && <Phone className="w-5 h-5" />}
                                        {insight.type === "stalled_deal" && <TrendingUp className="w-5 h-5" />}
                                    </div>
                                    <div>
                                        <div className="flex items-center gap-2">
                                            <h4 className="font-semibold text-sm text-slate-800 dark:text-slate-200">{insight.title}</h4>
                                            {insight.priority === "high" && (
                                                <Badge variant="destructive" className="h-5 px-1.5 text-[10px]">Urgent</Badge>
                                            )}
                                        </div>
                                        <p className="text-xs text-slate-500 dark:text-slate-400 line-clamp-1">{insight.description}</p>
                                    </div>
                                </div>
                                <Button size="icon" variant="ghost" className="opacity-0 group-hover:opacity-100 transition-opacity">
                                    <ArrowRight className="w-4 h-4 text-slate-400" />
                                </Button>
                            </div>
                        ))}

                        {insights.length === 0 && (
                            <div className="flex items-center gap-2 text-sm text-green-600 bg-green-50 p-3 rounded-lg">
                                <CheckCircle2 className="w-5 h-5" />
                                <span>You're all caught up!</span>
                            </div>
                        )}
                    </div>
                </div>
            </CardContent>
        </Card>
    );
};
